import { useState, useEffect } from "react";
import { fetchDashboardData } from "../../../services/dashboardService";
import { useSelector } from "react-redux";
import { useEffectiveStudentId } from "../../../components/Peoples/guardian/hooks/useEffectiveStudentId";

// Define the data structure for the dashboard response
export interface DashboardData {
  counts: {
    students: number;
    classes: number;
    teachers?: number; // Optional for roles that don't have teachers
    subjects?: number; // Optional for roles that have subjects
  };
  todaysPlanning: any[];
  incomingHomework: any[];
  incomingExamSessions?: any[];
  absencesOverview?: any[];
  pendingAbsences?: any[];
  dailyAbsences?: any[];
  inboxMessages?: any[];
}

const useDashboardData = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const user = useSelector((state: any) => state.authSlice.user);
  const selectedStudentId = useEffectiveStudentId();

  useEffect(() => {
    const getDashboardData = async () => {
      try {
        if (user?.role === "guardian" && !selectedStudentId) {
          setIsLoading(false);
          setData(null);
          return;
        }

        setIsLoading(true);

        // Decide if we need to send selectedStudentId
        const response = await fetchDashboardData(
          user?.role === "guardian" ? selectedStudentId : ""
        );

        const formattedData: DashboardData = {
          counts: {
            students: response.counts?.students ?? 0,
            classes: response.counts?.classes ?? 0,
            teachers: response.counts?.teachers ?? 0,
            subjects: response.counts?.subjects ?? 0,
            },
          todaysPlanning: response.todaysPlanning ?? [],
          incomingHomework: response.incomingHomework ?? [],
          incomingExamSessions: response.incomingExamSessions ?? [],
          absencesOverview: response.absencesOverview ?? [],
          pendingAbsences: response.pendingAbsences ?? [],
          dailyAbsences: response.todaysAbsences ?? [],
          inboxMessages: response.inboxMessages ?? [],
        };

        setData(formattedData);
      } catch (err) {
        setError("Failed to fetch dashboard data");
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      getDashboardData();
    }
  }, [user, selectedStudentId]); // Re-run if user or studentId changes!

  return { data, isLoading, error };
};

export default useDashboardData;
